/*
 * The single number registry. Every figure rendered on the site lives here; nothing is typed
 * into JSX. Every entry traces to FACTS.md. A figure carries a `receipt` only when it falls in
 * one of three classes: artifact (a public link), document (the CV this site serves), or
 * measurement (composition plus date, no link). No receipt means no dot. Never "my repo".
 */

export type ReceiptKind = 'artifact' | 'document' | 'measurement';

export interface Figure {
  /* The exact rendered string, e.g. '3,361'. */
  value: string;
  /* Mono label printed after or under the value, e.g. 'PROGRAMS MAPPED'. */
  unit: string;
  /* Present when the value animates. */
  countTo?: number;
  /* ISO date the figure was verified or measured. */
  asOf: string;
  /* Receipt text, mono uppercase, printed verbatim. Omitted when there is no checkable source. */
  receipt?: string;
  receiptKind?: ReceiptKind;
  /* Only for artifact receipts that end with an arrow. */
  receiptHref?: string;
}

export const RESUME_HREF = '/Guilherme_Niclewicz_Resume.pdf';

const VERIFIED = '2026-09-07';
const CHORIE_COUNTED = '2026-06-27';
const SAIDA_COUNTED = '2026-08-19';

const IMGRADAR_SNAPSHOT = 'SOURCE: IMGRADAR.COM PUBLISHED SNAPSHOT v11, READ 2026-09-07. →';
const IMGRADAR_URL = 'https://imgradar.com';
const CHORIE_DB = 'HOMECHORES PRODUCTION DATABASE, COUNTED 2026-06-27.';
const RESUME_DISNEY = 'SOURCE: RÉSUMÉ, DISNEY, FEB 2025 TO PRESENT. DOWNLOAD ↓';
const RESUME_OLIST = 'SOURCE: RÉSUMÉ, OLIST, FEB 2022 TO FEB 2025. DOWNLOAD ↓';
const RESUME_EXXON = 'SOURCE: RÉSUMÉ, EXXONMOBIL, DEC 2020 TO FEB 2022. DOWNLOAD ↓';
const RESUME_BRADESCO = 'SOURCE: RÉSUMÉ, BRADESCO, NOV 2019 TO DEC 2020. DOWNLOAD ↓';

const fig = (figure: Figure): Figure => figure;

const table = {
  /* ---------------------------------------------------------------- 02 pattern */
  'pattern.tests': fig({
    value: '4,000+',
    unit: 'AUTOMATED TESTS ACROSS THE FIVE PRODUCTS',
    countTo: 4000,
    asOf: VERIFIED,
    receipt: '407 PLUS 551 PLUS 822 PLUS 1,873 PLUS 909. COUNTED 2026-09-07.',
    receiptKind: 'measurement',
  }),
  'pattern.commits': fig({
    value: '2,800+',
    unit: 'COMMITS SINCE JUNE 2026',
    countTo: 2800,
    asOf: VERIFIED,
  }),
  'pattern.products': fig({
    value: '5',
    unit: 'PRODUCTS IN 20 MONTHS, BUILT SOLO',
    countTo: 5,
    asOf: VERIFIED,
  }),
  'pattern.months': fig({ value: '20', unit: 'MONTHS', countTo: 20, asOf: VERIFIED }),
  'pattern.ralphTests': fig({
    value: '386',
    unit: 'GREEN TESTS',
    countTo: 386,
    asOf: VERIFIED,
    receipt: '386 GREEN TESTS. 65 SESSIONS, 11 OF 11 TASKS SHIPPED ON HOMECHORES.',
    receiptKind: 'measurement',
  }),
  'pattern.ralphSessions': fig({ value: '65', unit: 'SESSIONS', countTo: 65, asOf: VERIFIED }),
  'pattern.ralphTasks': fig({ value: '11 of 11', unit: 'TASKS SHIPPED', asOf: VERIFIED }),

  /* ---------------------------------------------------------------- 03 imgradar */
  'imgradar.programs': fig({
    value: '3,361',
    unit: 'PROGRAMS MAPPED',
    countTo: 3361,
    asOf: VERIFIED,
    receipt: IMGRADAR_SNAPSHOT,
    receiptKind: 'artifact',
    receiptHref: IMGRADAR_URL,
  }),
  'imgradar.facts': fig({
    value: '33,302',
    unit: 'FACTS IN THE LEDGER',
    countTo: 33302,
    asOf: VERIFIED,
    receipt: IMGRADAR_SNAPSHOT,
    receiptKind: 'artifact',
    receiptHref: IMGRADAR_URL,
  }),
  'imgradar.confirmed': fig({
    value: '32,834',
    unit: 'CONFIRMED BY A SECOND READING',
    countTo: 32834,
    asOf: VERIFIED,
    receipt: IMGRADAR_SNAPSHOT,
    receiptKind: 'artifact',
    receiptHref: IMGRADAR_URL,
  }),
  'imgradar.confirmedPct': fig({
    value: '98.6%',
    unit: 'CONFIRMED BY A SECOND READING',
    countTo: 98.6,
    asOf: VERIFIED,
    receipt: IMGRADAR_SNAPSHOT,
    receiptKind: 'artifact',
    receiptHref: IMGRADAR_URL,
  }),
  'imgradar.pending': fig({
    value: '468',
    unit: 'FACTS STILL WAITING ON A SECOND READING',
    countTo: 468,
    asOf: VERIFIED,
  }),
  'imgradar.sourceUrls': fig({
    value: '14,544',
    unit: 'SOURCE URLS FROM 1,014 DOMAINS',
    countTo: 14544,
    asOf: VERIFIED,
    receipt: IMGRADAR_SNAPSHOT,
    receiptKind: 'artifact',
    receiptHref: IMGRADAR_URL,
  }),
  'imgradar.domains': fig({
    value: '1,014',
    unit: 'DOMAINS',
    countTo: 1014,
    asOf: VERIFIED,
    receipt: IMGRADAR_SNAPSHOT,
    receiptKind: 'artifact',
    receiptHref: IMGRADAR_URL,
  }),
  'imgradar.brazilian': fig({
    value: '264',
    unit: 'PROGRAMS WITH A BRAZILIAN CONFIRMED ON THE TEAM',
    countTo: 264,
    asOf: VERIFIED,
    receipt: IMGRADAR_SNAPSHOT,
    receiptKind: 'artifact',
    receiptHref: IMGRADAR_URL,
  }),
  'imgradar.tier25': fig({
    value: '1,565',
    unit: 'PROGRAMS WITH 25%+ OF THE TEAM TRAINED ABROAD',
    countTo: 1565,
    asOf: VERIFIED,
    receipt: IMGRADAR_SNAPSHOT,
    receiptKind: 'artifact',
    receiptHref: IMGRADAR_URL,
  }),
  /* The other two tiers of the same published split, corrected to the shipped dataset:
     1,533 teal and 263 grey drawn, plus the two programs without coordinates, both gold. */
  'imgradar.tierEvidence': fig({
    value: '1,533',
    unit: 'PROGRAMS WITH EVIDENCE IT ACCEPTS FOREIGN GRADUATES',
    countTo: 1533,
    asOf: VERIFIED,
    receipt: IMGRADAR_SNAPSHOT,
    receiptKind: 'artifact',
    receiptHref: IMGRADAR_URL,
  }),
  'imgradar.tierNone': fig({
    value: '263',
    unit: 'PROGRAMS MAPPED WITH NO EVIDENCE YET',
    countTo: 263,
    asOf: VERIFIED,
    receipt: IMGRADAR_SNAPSHOT,
    receiptKind: 'artifact',
    receiptHref: IMGRADAR_URL,
  }),
  'imgradar.tier50': fig({
    value: '1,017',
    unit: 'PROGRAMS WITH 50%+ OF THE TEAM TRAINED ABROAD',
    countTo: 1017,
    asOf: VERIFIED,
  }),
  'imgradar.counted': fig({
    value: '2,891',
    unit: 'PROGRAMS COUNTED RESIDENT BY RESIDENT',
    countTo: 2891,
    asOf: VERIFIED,
  }),
  'imgradar.institutions': fig({
    value: '979',
    unit: 'INSTITUTIONS',
    countTo: 979,
    asOf: VERIFIED,
  }),
  'imgradar.specialties': fig({ value: '33', unit: 'SPECIALTIES', countTo: 33, asOf: VERIFIED }),
  'imgradar.cities': fig({ value: '697', unit: 'CITIES', countTo: 697, asOf: VERIFIED }),
  'imgradar.states': fig({
    value: '52',
    unit: 'STATES AND TERRITORIES',
    countTo: 52,
    asOf: VERIFIED,
  }),
  'imgradar.honestNegatives': fig({
    value: '6,871',
    unit: 'TIMES THE ANSWER WAS NOTHING',
    countTo: 6871,
    asOf: VERIFIED,
  }),
  'imgradar.avgSources': fig({
    value: '5.3',
    unit: 'CITED SOURCES PER PROGRAM ON AVERAGE',
    countTo: 5.3,
    asOf: VERIFIED,
  }),
  'imgradar.j1': fig({
    value: '2,592',
    unit: 'PROGRAMS SPONSORING J-1',
    countTo: 2592,
    asOf: VERIFIED,
  }),
  'imgradar.h1b': fig({
    value: '501',
    unit: 'PROGRAMS SPONSORING H-1B',
    countTo: 501,
    asOf: VERIFIED,
  }),
  'imgradar.slots': fig({
    value: '28,213',
    unit: 'PGY-1 SLOTS A YEAR RECORDED',
    countTo: 28213,
    asOf: VERIFIED,
  }),
  'imgradar.pgy1Salary': fig({
    value: 'US$ 69,725',
    unit: 'MEDIAN PGY-1 SALARY OVER 2,142 PROGRAMS',
    asOf: VERIFIED,
  }),
  'imgradar.agents': fig({
    value: '3',
    unit: 'AUTONOMOUS RESEARCH AGENTS',
    countTo: 3,
    asOf: VERIFIED,
  }),
  'imgradar.factTypes': fig({
    value: '12',
    unit: 'DOSSIER FACT TYPES PER PROGRAM',
    countTo: 12,
    asOf: VERIFIED,
  }),
  'imgradar.tests': fig({
    value: '407',
    unit: 'AUTOMATED TESTS',
    countTo: 407,
    asOf: VERIFIED,
    receipt: '407 AUTOMATED TESTS: 245 PYTEST PLUS 162 VITEST. COUNTED 2026-09-07.',
    receiptKind: 'measurement',
  }),
  'imgradar.prs': fig({ value: '37', unit: 'PULL REQUESTS', countTo: 37, asOf: VERIFIED }),
  'imgradar.days': fig({
    value: '13 days',
    unit: 'FIRST COMMIT TO A LIVE STRIPE SUBSCRIPTION',
    countTo: 13,
    asOf: '2026-09-01',
    receipt: 'SOURCE: IMGRADAR.COM CHECKOUT, LIVE SINCE 2026-09-01. →',
    receiptKind: 'artifact',
    receiptHref: IMGRADAR_URL,
  }),
  'imgradar.stripeLive': fig({
    value: 'SEP 1 2026',
    unit: 'STRIPE LIVE',
    asOf: '2026-09-01',
    receipt: 'SOURCE: IMGRADAR.COM CHECKOUT, LIVE SINCE 2026-09-01. →',
    receiptKind: 'artifact',
    receiptHref: IMGRADAR_URL,
  }),
  'imgradar.price': fig({ value: 'US$ 29', unit: 'A YEAR', asOf: '2026-09-01' }),
  'imgradar.subscribers': fig({
    value: '70+',
    unit: 'PAYING SUBSCRIBERS IN THE FIRST MONTH',
    countTo: 70,
    asOf: '2026-09-23',
    receipt: 'STRIPE SUBSCRIPTIONS ON IMGRADAR.COM, COUNTED 2026-09-23.',
    receiptKind: 'measurement',
  }),
  'imgradar.sample': fig({
    value: '320',
    unit: 'PROGRAMS IN THE SAMPLE',
    countTo: 320,
    asOf: VERIFIED,
  }),
  'imgradar.bulkKb': fig({
    value: '219 KB',
    unit: 'FREE BULK FOR ALL 3,361 PROGRAMS, GZIPPED',
    asOf: VERIFIED,
  }),

  /* ---------------------------------------------------------------- 04 radar407 */
  'r407.coverage': fig({
    value: '98.8%',
    unit: 'OF NEW SINGLE-FAMILY HOMES IN THE THREE COUNTIES. 10,075 OF 10,199 UNITS.',
    countTo: 98.8,
    asOf: '2025-12-31',
    receipt:
      'SOURCE: US CENSUS BUREAU, BUILDING PERMITS SURVEY 2025. 10,075 OF 10,199 UNITS ACROSS ORANGE, SEMINOLE AND OSCEOLA.',
    receiptKind: 'artifact',
  }),
  'r407.coveredUnits': fig({
    value: '10,075',
    unit: 'UNITS COVERED',
    countTo: 10075,
    asOf: '2025-12-31',
  }),
  'r407.totalUnits': fig({
    value: '10,199',
    unit: 'NEW SINGLE-FAMILY UNITS, THREE COUNTIES, 2025',
    countTo: 10199,
    asOf: '2025-12-31',
  }),
  'r407.records7d': fig({
    value: '2,906,614',
    unit: 'RECORDS INGESTED IN THE LAST SEVEN DAYS',
    countTo: 2906614,
    asOf: VERIFIED,
    receipt: 'MEASURED ON THE PRODUCTION PIPELINE, 7 DAYS TO 2026-09-07. 157 RUNS.',
    receiptKind: 'measurement',
  }),
  'r407.llmCalls': fig({
    value: '0',
    unit: 'LLM CALLS IN THE DATA PATH, ON PURPOSE',
    countTo: 0,
    asOf: VERIFIED,
  }),
  'r407.runs7d': fig({
    value: '157',
    unit: 'RUNS, LAST 7 DAYS',
    countTo: 157,
    asOf: VERIFIED,
    receipt: 'CLOUDWATCH RUN LOG, 7 DAYS TO 2026-09-07.',
    receiptKind: 'measurement',
  }),
  'r407.runsGreen': fig({
    value: '149',
    unit: 'GREEN',
    countTo: 149,
    asOf: VERIFIED,
    receipt: 'CLOUDWATCH RUN LOG, 7 DAYS TO 2026-09-07.',
    receiptKind: 'measurement',
  }),
  'r407.runsYellow': fig({
    value: '6',
    unit: 'YELLOW',
    countTo: 6,
    asOf: VERIFIED,
    receipt: 'CLOUDWATCH RUN LOG, 7 DAYS TO 2026-09-07.',
    receiptKind: 'measurement',
  }),
  'r407.runsRed': fig({
    value: '0',
    unit: 'RED',
    countTo: 0,
    asOf: VERIFIED,
    receipt: 'CLOUDWATCH RUN LOG, 7 DAYS TO 2026-09-07.',
    receiptKind: 'measurement',
  }),
  'r407.alarms': fig({
    value: '7',
    unit: 'CLOUDWATCH ALARMS, ALL OK',
    countTo: 7,
    asOf: VERIFIED,
  }),
  'r407.sources': fig({ value: '25', unit: 'SOURCE COLLECTORS', countTo: 25, asOf: VERIFIED }),
  'r407.engines': fig({ value: '17', unit: 'SCRAPING ENGINES', countTo: 17, asOf: VERIFIED }),
  'r407.jurisdictions': fig({
    value: '13',
    unit: 'PERMIT JURISDICTIONS',
    countTo: 13,
    asOf: VERIFIED,
  }),
  'r407.counties': fig({ value: '3', unit: 'COUNTIES', countTo: 3, asOf: VERIFIED }),
  'r407.daysUnattended': fig({
    value: '27',
    unit: 'STRAIGHT DAYS OF UNATTENDED PRODUCTION',
    countTo: 27,
    asOf: VERIFIED,
  }),
  'r407.pipelineSince': fig({
    value: 'AUG 12 2026',
    unit: 'PIPELINE LIVE IN AWS',
    asOf: '2026-08-12',
  }),
  'r407.silverRows': fig({
    value: '1,151,132',
    unit: 'ROWS REPRODUCED HASH-IDENTICAL',
    countTo: 1151132,
    asOf: VERIFIED,
  }),
  'r407.licenseResolved': fig({
    value: '65.9%',
    unit: 'OF PERMITS RESOLVE TO A STATE LICENSE',
    countTo: 65.9,
    asOf: VERIFIED,
  }),
  'r407.leadsWithContact': fig({
    value: '43.3%',
    unit: 'OF 30-DAY LEADS CARRY A PHONE OR EMAIL',
    countTo: 43.3,
    asOf: VERIFIED,
  }),
  'r407.nocMedianDays': fig({
    value: '38',
    unit: 'DAYS A NOC LANDS BEFORE THE PERMIT, MEDIAN',
    countTo: 38,
    asOf: VERIFIED,
  }),
  'r407.topBuildersShare': fig({
    value: '74 to 80%',
    unit: 'OF NEW HOMES HELD BY THE TOP TEN BUILDERS',
    asOf: VERIFIED,
  }),
  'r407.tests': fig({
    value: '551',
    unit: 'PASSING TESTS',
    countTo: 551,
    asOf: VERIFIED,
    receipt: '551 PASSING TESTS, ABOUT ONE TEST LINE PER PRODUCTION LINE. COUNTED 2026-09-07.',
    receiptKind: 'measurement',
  }),
  'r407.commits': fig({ value: '268', unit: 'COMMITS IN 27 DAYS', countTo: 268, asOf: VERIFIED }),
  'r407.prs': fig({ value: '66', unit: 'MERGED PULL REQUESTS', countTo: 66, asOf: VERIFIED }),
  'r407.daysToCron': fig({
    value: '3 days',
    unit: 'FIRST COMMIT TO PRODUCTION CRON',
    countTo: 3,
    asOf: '2026-08-12',
  }),
  'r407.backfillMonths': fig({
    value: '6',
    unit: 'MONTHS OF BACKFILLED HISTORY',
    countTo: 6,
    asOf: VERIFIED,
  }),

  /* ---------------------------------------------------------------- 05 saidacerta */
  'saida.goldenVectors': fig({
    value: '4,975',
    unit: 'GOLDEN VECTORS PROVING THE RULE ENGINE MATCHES ITS TYPESCRIPT MIRROR',
    countTo: 4975,
    asOf: SAIDA_COUNTED,
    receipt:
      '4,975 GOLDEN VECTORS ASSERTING THE PYTHON ENGINE AND ITS TYPESCRIPT MIRROR AGREE. COUNTED 2026-08-19.',
    receiptKind: 'measurement',
  }),
  'saida.engineLines': fig({
    value: '8,039',
    unit: 'LINES OF PURE-PYTHON FISCAL ENGINE WITH ZERO DJANGO IMPORTS',
    countTo: 8039,
    asOf: SAIDA_COUNTED,
  }),
  'saida.daysToStripe': fig({
    value: '6 days',
    unit: 'FIRST COMMIT TO STRIPE LIVE',
    countTo: 6,
    asOf: '2026-07-16',
    receipt: 'SOURCE: SAIDACERTA.COM CHECKOUT, LIVE SINCE 2026-07-16. →',
    receiptKind: 'artifact',
    receiptHref: 'https://saidacerta.com',
  }),
  'saida.stripeLive': fig({
    value: 'JUL 16 2026',
    unit: 'STRIPE LIVE',
    asOf: '2026-07-16',
    receipt: 'SOURCE: SAIDACERTA.COM CHECKOUT, LIVE SINCE 2026-07-16. →',
    receiptKind: 'artifact',
    receiptHref: 'https://saidacerta.com',
  }),
  'saida.tests': fig({
    value: '788',
    unit: 'BACKEND TESTS',
    countTo: 788,
    asOf: SAIDA_COUNTED,
    receipt: '788 BACKEND TESTS PLUS 34 VITEST. COUNTED 2026-08-19.',
    receiptKind: 'measurement',
  }),
  'saida.selicMonths': fig({
    value: '102',
    unit: 'HAND-VERIFIED MONTHS OF SELIC RATES',
    countTo: 102,
    asOf: SAIDA_COUNTED,
  }),
  'saida.leaveYearly': fig({
    value: '400,000',
    unit: 'BRAZILIANS LEAVE EVERY YEAR',
    countTo: 400000,
    asOf: '2021-12-31',
    receipt:
      'SOURCE: BRAZILIAN EXIT DECLARATION FILINGS, 2021. ABOUT 4.9 MILLION BRAZILIANS LIVE ABROAD.',
    receiptKind: 'artifact',
  }),
  'saida.exitFilings': fig({
    value: '28,000',
    unit: 'EXIT DECLARATIONS FILED IN 2021',
    countTo: 28000,
    asOf: '2021-12-31',
    receipt:
      'SOURCE: BRAZILIAN EXIT DECLARATION FILINGS, 2021. ABOUT 4.9 MILLION BRAZILIANS LIVE ABROAD.',
    receiptKind: 'artifact',
  }),
  'saida.abroad': fig({
    value: '4.9 million',
    unit: 'BRAZILIANS LIVE ABROAD',
    asOf: '2021-12-31',
  }),
  'saida.commits': fig({
    value: '483',
    unit: 'COMMITS IN SIX WEEKS',
    countTo: 483,
    asOf: SAIDA_COUNTED,
  }),
  'saida.prs': fig({
    value: '108',
    unit: 'MERGED PULL REQUESTS',
    countTo: 108,
    asOf: SAIDA_COUNTED,
  }),
  'saida.peakWeekCommits': fig({
    value: '179',
    unit: 'COMMITS IN THE PEAK WEEK',
    countTo: 179,
    asOf: SAIDA_COUNTED,
  }),
  'saida.weeks': fig({
    value: '6 weeks',
    unit: 'PROTOTYPE TO PRODUCTION',
    countTo: 6,
    asOf: SAIDA_COUNTED,
  }),
  'saida.govUrls': fig({
    value: '197',
    unit: 'DISTINCT GOV.BR URLS ACROSS THE PRODUCT',
    countTo: 197,
    asOf: SAIDA_COUNTED,
  }),
  'saida.faqAnswers': fig({
    value: '500',
    unit: 'VERIFIED FAQ ANSWERS',
    countTo: 500,
    asOf: SAIDA_COUNTED,
  }),
  'saida.dossiers': fig({
    value: '15',
    unit: 'SOURCE DOSSIERS IN THE RAG',
    countTo: 15,
    asOf: SAIDA_COUNTED,
  }),
  'saida.questions': fig({
    value: '18',
    unit: 'ADAPTIVE INTERVIEW QUESTIONS',
    countTo: 18,
    asOf: SAIDA_COUNTED,
  }),
  'saida.stepTypes': fig({
    value: '26',
    unit: 'TRAIL STEP TYPES',
    countTo: 26,
    asOf: SAIDA_COUNTED,
  }),
  'saida.documents': fig({
    value: '7',
    unit: 'GENERATED DOCUMENTS PER CASE',
    countTo: 7,
    asOf: SAIDA_COUNTED,
  }),
  'saida.guides': fig({ value: '6', unit: 'PUBLISHED GUIDES', countTo: 6, asOf: SAIDA_COUNTED }),
  'saida.guideSources': fig({
    value: '60',
    unit: 'OFFICIAL SOURCES CITED BY THE GUIDES',
    countTo: 60,
    asOf: SAIDA_COUNTED,
  }),
  'saida.priceTiers': fig({
    value: 'US$ 97 / 137 / 177 / 217',
    unit: 'BY YEARS OVERDUE',
    asOf: '2026-07-16',
  }),
  'saida.withholding': fig({
    value: '15%',
    unit: 'WITHHELD ON RENT TO A NON-RESIDENT',
    asOf: SAIDA_COUNTED,
  }),
  'saida.fineDaily': fig({ value: '0.33%', unit: 'FINE A DAY', asOf: SAIDA_COUNTED }),
  'saida.fineCap': fig({ value: '20%', unit: 'FINE CAP', asOf: SAIDA_COUNTED }),

  /* ---------------------------------------------------------------- 06 findy */
  'findy.daysToStore': fig({
    value: '25 days',
    unit: 'FIRST COMMIT TO THE APP STORE',
    countTo: 25,
    asOf: '2026-07-20',
    receipt: 'SOURCE: APP STORE LISTING id6789587559, RELEASED 2026-07-20. →',
    receiptKind: 'artifact',
    receiptHref: 'https://apps.apple.com/app/id6789587559',
  }),
  'findy.storeLive': fig({
    value: 'JUL 20 2026',
    unit: 'ON THE APP STORE',
    asOf: '2026-07-20',
    receipt: 'SOURCE: APP STORE LISTING id6789587559, RELEASED 2026-07-20. →',
    receiptKind: 'artifact',
    receiptHref: 'https://apps.apple.com/app/id6789587559',
  }),
  'findy.tests': fig({
    value: '788',
    unit: 'BACKEND TESTS AGAINST REAL POSTGRES AND PGVECTOR IN CI',
    countTo: 788,
    asOf: VERIFIED,
    receipt: '788 BACKEND TESTS AGAINST REAL POSTGRES AND PGVECTOR IN CI. COUNTED 2026-09-07.',
    receiptKind: 'measurement',
  }),
  'findy.tenantAssertions': fig({
    value: '73',
    unit: 'TENANT-ISOLATION ASSERTIONS IN THE TEST SUITE',
    countTo: 73,
    asOf: VERIFIED,
  }),
  'findy.testRatio': fig({ value: '1.17 to 1', unit: 'TEST TO CODE', asOf: VERIFIED }),
  'findy.jestCases': fig({ value: '121', unit: 'MOBILE JEST CASES', countTo: 121, asOf: VERIFIED }),
  'findy.commits': fig({
    value: '274',
    unit: 'COMMITS IN FIVE WEEKS',
    countTo: 274,
    asOf: VERIFIED,
  }),
  'findy.prs': fig({ value: '74', unit: 'MERGED PULL REQUESTS', countTo: 74, asOf: VERIFIED }),
  'findy.clients': fig({ value: '3', unit: 'CLIENTS ON ONE API', countTo: 3, asOf: VERIFIED }),
  'findy.shelves': fig({ value: '10', unit: 'LIFE-DOMAIN SHELVES', countTo: 10, asOf: VERIFIED }),
  'findy.fields': fig({
    value: '10',
    unit: 'STRUCTURED FIELDS EXTRACTED',
    countTo: 10,
    asOf: VERIFIED,
  }),
  'findy.rrfK': fig({ value: '60', unit: 'RRF k', countTo: 60, asOf: VERIFIED }),
  'findy.halfvecDims': fig({
    value: '3072',
    unit: 'HALFVEC DIMENSIONS',
    countTo: 3072,
    asOf: VERIFIED,
  }),
  'findy.sampleDocs': fig({
    value: '8',
    unit: 'DOCUMENTS IN THE SAMPLE DRAWER',
    countTo: 8,
    asOf: VERIFIED,
  }),
  'findy.conceptDims': fig({
    value: '12',
    unit: 'CONCEPT DIMENSIONS',
    countTo: 12,
    asOf: VERIFIED,
  }),
  'findy.formats': fig({ value: '6', unit: 'FILE FORMATS', countTo: 6, asOf: VERIFIED }),
  'findy.maxMb': fig({ value: '25 MB', unit: 'PER FILE', countTo: 25, asOf: VERIFIED }),
  'findy.deploys': fig({ value: '63', unit: 'ECS DEPLOYS', countTo: 63, asOf: VERIFIED }),
  'findy.rateLimitScopes': fig({
    value: '22',
    unit: 'NAMED RATE-LIMIT SCOPES',
    countTo: 22,
    asOf: VERIFIED,
  }),
  'findy.appStoreId': fig({ value: 'id6789587559', unit: 'APP STORE', asOf: '2026-07-20' }),

  /* ---------------------------------------------------------------- 07 homechores */
  'chorie.users': fig({
    value: '1,953',
    unit: 'PEOPLE USING IT',
    countTo: 1953,
    asOf: CHORIE_COUNTED,
    receipt: CHORIE_DB,
    receiptKind: 'measurement',
  }),
  'chorie.publishedUsers': fig({
    value: '2,930',
    unit: 'USERS',
    countTo: 2930,
    asOf: '2026-09-08',
    receipt: 'SOURCE: RÉSUMÉ, HOMECHORES. DOWNLOAD ↓',
    receiptKind: 'document',
    receiptHref: RESUME_HREF,
  }),
  'chorie.households': fig({
    value: '1,513',
    unit: 'HOUSEHOLDS',
    countTo: 1513,
    asOf: CHORIE_COUNTED,
    receipt: CHORIE_DB,
    receiptKind: 'measurement',
  }),
  'chorie.signupsMonth': fig({
    value: '978',
    unit: 'SIGNUPS IN ONE MONTH FROM A SINGLE ORGANIC VIDEO',
    countTo: 978,
    asOf: '2026-02-28',
    receipt: 'HOMECHORES PRODUCTION DATABASE, FEBRUARY 2026.',
    receiptKind: 'measurement',
  }),
  'chorie.taskVolumeMultiple': fig({
    value: '8.8x',
    unit: 'THE TASK VOLUME IN MULTI-PERSON HOMES VERSUS SOLO HOMES',
    countTo: 8.8,
    asOf: CHORIE_COUNTED,
    receipt: CHORIE_DB,
    receiptKind: 'measurement',
  }),
  'chorie.inviteAcceptance': fig({
    value: '33.1%',
    unit: 'INVITE ACCEPTANCE',
    countTo: 33.1,
    asOf: CHORIE_COUNTED,
  }),
  'chorie.stores': fig({
    value: 'iOS and Android',
    unit: 'LIVE IN BOTH STORES',
    asOf: VERIFIED,
    receipt: 'SOURCE: APP STORE LISTING id6753123892 AND GOOGLE PLAY. →',
    receiptKind: 'artifact',
    receiptHref: 'https://apps.apple.com/app/id6753123892',
  }),
  'chorie.tools': fig({ value: '15', unit: 'USER-FACING AI TOOLS', countTo: 15, asOf: VERIFIED }),
  'chorie.languages': fig({ value: '4', unit: 'LANGUAGES', countTo: 4, asOf: VERIFIED }),
  'chorie.testFunctions': fig({
    value: '1,873',
    unit: 'TEST FUNCTIONS',
    countTo: 1873,
    asOf: VERIFIED,
  }),
  'chorie.coverage': fig({ value: '93%', unit: 'BACKEND COVERAGE', countTo: 93, asOf: VERIFIED }),
  'chorie.translationKeys': fig({
    value: '1,652',
    unit: 'TRANSLATION KEYS PER LOCALE',
    countTo: 1652,
    asOf: VERIFIED,
  }),
  'chorie.benchmarkAccuracy': fig({
    value: '18 of 18',
    unit: 'TOOL AND ARGUMENT ACCURACY IN THE BENCHMARK',
    asOf: VERIFIED,
  }),
  'chorie.cheaper': fig({ value: '48%', unit: 'CHEAPER', countTo: 48, asOf: VERIFIED }),
  'chorie.costPerTurn': fig({ value: '$0.0025', unit: 'PER TURN, MEASURED', asOf: VERIFIED }),
  'chorie.storeBuilds': fig({
    value: '16',
    unit: 'STORE BUILDS IN TWO MONTHS',
    countTo: 16,
    asOf: VERIFIED,
  }),
  'chorie.screens': fig({ value: '81', unit: 'SCREENS', countTo: 81, asOf: VERIFIED }),
  'chorie.appStoreId': fig({ value: 'id6753123892', unit: 'APP STORE', asOf: VERIFIED }),
  'chorie.playId': fig({
    value: 'com.bernieshomechores.android',
    unit: 'GOOGLE PLAY',
    asOf: VERIFIED,
  }),

  /* ---------------------------------------------------------------- 08 record */
  'record.disney.engineers': fig({
    value: '200+',
    unit: 'ENGINEERS AND ANALYSTS ON THE PLATFORM',
    countTo: 200,
    asOf: VERIFIED,
    receipt: RESUME_DISNEY,
    receiptKind: 'document',
    receiptHref: RESUME_HREF,
  }),
  'record.disney.deployCut': fig({
    value: '60%',
    unit: 'CUT IN DEPLOY TIME',
    countTo: 60,
    asOf: VERIFIED,
    receipt: RESUME_DISNEY,
    receiptKind: 'document',
    receiptHref: RESUME_HREF,
  }),
  'record.olist.messages': fig({
    value: '1M+',
    unit: 'CUSTOMER MESSAGES A DAY',
    asOf: VERIFIED,
    receipt: RESUME_OLIST,
    receiptKind: 'document',
    receiptHref: RESUME_HREF,
  }),
  'record.olist.services': fig({
    value: '20 → 2',
    unit: 'SERVICES',
    asOf: VERIFIED,
    receipt: RESUME_OLIST,
    receiptKind: 'document',
    receiptHref: RESUME_HREF,
  }),
  'record.olist.delivery': fig({
    value: '20s → 2s',
    unit: 'DELIVERY',
    asOf: VERIFIED,
    receipt: RESUME_OLIST,
    receiptKind: 'document',
    receiptHref: RESUME_HREF,
  }),
  'record.olist.engineers': fig({ value: '20', unit: 'ENGINEERS', countTo: 20, asOf: VERIFIED }),
  'record.olist.publish': fig({ value: 'weeks → hours', unit: 'TIME TO PUBLISH', asOf: VERIFIED }),
  'record.olist.pagespeed': fig({ value: 'E → A', unit: 'PAGESPEED', asOf: VERIFIED }),
  'record.exxon.employees': fig({
    value: '100K',
    unit: 'EMPLOYEES SECURED',
    countTo: 100,
    asOf: VERIFIED,
    receipt: RESUME_EXXON,
    receiptKind: 'document',
    receiptHref: RESUME_HREF,
  }),
  'record.exxon.employeesFull': fig({
    value: '100,000',
    unit: 'EMPLOYEES',
    countTo: 100000,
    asOf: VERIFIED,
    receipt: RESUME_EXXON,
    receiptKind: 'document',
    receiptHref: RESUME_HREF,
  }),
  'record.exxon.applications': fig({
    value: '7,000',
    unit: 'APPLICATIONS',
    countTo: 7000,
    asOf: VERIFIED,
    receipt: RESUME_EXXON,
    receiptKind: 'document',
    receiptHref: RESUME_HREF,
  }),
  'record.exxon.setup': fig({ value: 'days → minutes', unit: 'SETUP TIME', asOf: VERIFIED }),
  'record.exxon.portMappings': fig({
    value: '5,000+',
    unit: 'PORT MAPPINGS PER MINUTE',
    countTo: 5000,
    asOf: VERIFIED,
  }),
  'record.exxon.ownersTrained': fig({
    value: '200+',
    unit: 'APPLICATION OWNERS TRAINED',
    countTo: 200,
    asOf: VERIFIED,
  }),
  'record.bradesco.clients': fig({
    value: '35M',
    unit: 'CLIENTS',
    countTo: 35,
    asOf: VERIFIED,
    receipt: RESUME_BRADESCO,
    receiptKind: 'document',
    receiptHref: RESUME_HREF,
  }),
  'record.bradesco.runtimeCut': fig({
    value: '90%',
    unit: 'LESS PROCESSING TIME',
    countTo: 90,
    asOf: '2023-06-01',
  }),
  'record.bradesco.jobs': fig({
    value: '210',
    unit: 'DATA-PREP JOBS IN UNDER 4 HOURS',
    countTo: 210,
    asOf: '2023-06-01',
  }),
  'record.bradesco.award': fig({
    value: 'MOST INNOVATIVE IT · 2019 · LARGE BANK',
    unit: 'AWARD',
    asOf: '2019-12-31',
    receipt: "SOURCE: BRAZIL'S MOST INNOVATIVE IT AWARD 2019, LARGE-BANK CATEGORY.",
    receiptKind: 'artifact',
  }),
  'record.residency.deploys': fig({
    value: '42 deploys, 1 failure',
    unit: 'RUN BY SOMEONE ELSE',
    asOf: VERIFIED,
  }),

  /* ---------------------------------------------------------------- 09 to 11 */
  'writing.count': fig({ value: '4', unit: 'ARTICLES', countTo: 4, asOf: VERIFIED }),
  'ask.products': fig({ value: '5', unit: 'PRODUCTS', countTo: 5, asOf: VERIFIED }),
  'ask.employers': fig({ value: '4', unit: 'EMPLOYERS', countTo: 4, asOf: VERIFIED }),
  'ask.articles': fig({ value: '4', unit: 'ARTICLES', countTo: 4, asOf: VERIFIED }),
  'ask.resume': fig({ value: '1', unit: 'RESUME', countTo: 1, asOf: VERIFIED }),
  'person.years': fig({ value: 'Ten years', unit: 'AN ENGINEER', asOf: VERIFIED }),
  'person.bjjYears': fig({ value: 'twelve', unit: 'YEARS ON THE MAT', asOf: VERIFIED }),
};

export type FigureId = keyof typeof table;

export const figures: Record<FigureId, Figure> = table;

export const figureIds = Object.keys(table) as FigureId[];

export function hasReceipt(id: FigureId): boolean {
  return Boolean(figures[id].receipt);
}
